import { format, isValid, parseISO } from "date-fns";
import { api } from "@/lib/orgApi";
import { getDiagnosisCodeByCode, ICD10CM_SYSTEM } from "@/lib/diagnosisApi";
import { encounterNumberOf } from "@/lib/encounterApi";
import {
  BIRTHSEX,
  ETHNICITY_CATEGORIES,
  ETHNICITY_URL,
  BIRTHSEX_URL,
  GENDER_IDENTITIES,
  GENDER_IDENTITY_URL,
  GENDERS,
  LANGUAGES,
  MARITAL_STATUSES,
  PRONOUNS,
  PRONOUNS_URL,
  RACE_CATEGORIES,
  RACE_URL,
  RELATIONSHIP_TYPES,
  SSN_SYSTEM,
  mrnOf,
} from "@/lib/patientApi";

export const NA = "Not available";

const disp = (list, code) => list.find((x) => x.code === code)?.display;

export function fetchPatient(patientId) {
  return api.get(`/patients/${encodeURIComponent(patientId)}`).then((r) => r.data);
}

export function fetchPatientEncounters(patientId) {
  return api
    .get("/encounters", { params: { patient: patientId } })
    .then((r) => r.data);
}

export function patientFullName(r) {
  const n = r?.name?.[0];
  if (!n) return "";
  return [...(n.given || []), n.family].filter(Boolean).join(" ");
}

export function patientInitials(r) {
  const n = r?.name?.[0];
  const parts = [...(n?.given || []).slice(0, 1), n?.family].filter(Boolean);
  const letters = parts.map((p) => p[0]?.toUpperCase() || "").join("");
  return letters || "PT";
}

export function ageFromBirthDate(iso) {
  if (!iso) return null;
  const parsed = parseISO(iso);
  if (!isValid(parsed)) return null;
  const today = new Date();
  let years = today.getFullYear() - parsed.getFullYear();
  const monthDiff = today.getMonth() - parsed.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < parsed.getDate())) {
    years -= 1;
  }
  return years >= 0 ? years : null;
}

export function formatDate(iso, pattern = "MM/dd/yyyy") {
  if (!iso) return NA;
  const parsed = parseISO(iso);
  if (!isValid(parsed)) return NA;
  return format(parsed, pattern);
}

export function formatGender(code) {
  if (!code) return NA;
  return disp(GENDERS, code) || code.charAt(0).toUpperCase() + code.slice(1);
}

export function formatAddress(r) {
  const addr = r?.address?.[0];
  if (!addr) return NA;
  const line = (addr.line || []).filter(Boolean).join(", ");
  const cityLine = [addr.city, addr.state].filter(Boolean).join(", ");
  const cityPostal = [cityLine, addr.postalCode].filter(Boolean).join(" ");
  const parts = [line, cityPostal, addr.country].filter(Boolean);
  return parts.length ? parts.join(", ") : NA;
}

function telecomOf(r, system, use) {
  const list = r?.telecom || [];
  const match = list.find((t) => t.system === system && (!use || t.use === use));
  return match?.value || list.find((t) => t.system === system)?.value || "";
}

export function patientPhone(r) {
  return telecomOf(r, "phone", "mobile") || telecomOf(r, "phone") || "";
}

export function patientEmail(r) {
  return telecomOf(r, "email") || "";
}

export function preferredLanguage(r) {
  const comms = r?.communication || [];
  const preferred = comms.find((c) => c.preferred) || comms[0];
  const coding = preferred?.language?.coding?.[0];
  if (!coding && !preferred?.language?.text) return NA;
  return (
    preferred?.language?.text ||
    coding?.display ||
    disp(LANGUAGES, coding?.code) ||
    coding?.code ||
    NA
  );
}

export function managingOrganizationName(r) {
  return r?.managingOrganization?.display || NA;
}

export function ssnOf(r) {
  const ident = (r?.identifier || []).find(
    (i) =>
      i.system === SSN_SYSTEM ||
      (i.type?.coding || []).some((c) => c.code === "SS"),
  );
  return ident?.value || "";
}

function extensionOf(r, url) {
  return (r?.extension || []).find((e) => e.url === url);
}

function subExtension(ext, url) {
  return (ext?.extension || []).find((x) => x.url === url);
}

export function raceLabel(r) {
  const race = extensionOf(r, RACE_URL);
  const omb = subExtension(race, "ombCategory")?.valueCoding;
  const text = subExtension(race, "text")?.valueString;
  return text || omb?.display || disp(RACE_CATEGORIES, omb?.code) || "";
}

export function ethnicityLabel(r) {
  const eth = extensionOf(r, ETHNICITY_URL);
  const omb = subExtension(eth, "ombCategory")?.valueCoding;
  const text = subExtension(eth, "text")?.valueString;
  return text || omb?.display || disp(ETHNICITY_CATEGORIES, omb?.code) || "";
}

export function birthsexLabel(r) {
  const code = extensionOf(r, BIRTHSEX_URL)?.valueCode;
  return code ? disp(BIRTHSEX, code) || code : "";
}

export function genderIdentityLabel(r) {
  const gi = extensionOf(r, GENDER_IDENTITY_URL)?.valueCodeableConcept;
  const coding = gi?.coding?.[0];
  return gi?.text || coding?.display || disp(GENDER_IDENTITIES, coding?.code) || "";
}

export function pronounsLabel(r) {
  const pr = extensionOf(r, PRONOUNS_URL);
  const value = subExtension(pr, "value")?.valueCodeableConcept;
  const coding = value?.coding?.[0];
  return value?.text || coding?.display || disp(PRONOUNS, coding?.code) || "";
}

export function maritalLabel(r) {
  const coding = r?.maritalStatus?.coding?.[0];
  return (
    r?.maritalStatus?.text ||
    coding?.display ||
    disp(MARITAL_STATUSES, coding?.code) ||
    ""
  );
}

export function emergencyContactOf(r) {
  const contact = r?.contact?.[0];
  if (!contact) {
    return { name: "", relationship: "", phone: "", email: "", present: false };
  }
  const name = [...(contact.name?.given || []), contact.name?.family]
    .filter(Boolean)
    .join(" ");
  const rel = contact.relationship?.[0];
  const relCoding = rel?.coding?.[0];
  const relationship =
    rel?.text ||
    relCoding?.display ||
    disp(RELATIONSHIP_TYPES, relCoding?.code) ||
    "";
  const phone =
    (contact.telecom || []).find((t) => t.system === "phone")?.value ||
    (contact.telecom || [])[0]?.value ||
    "";
  const email = (contact.telecom || []).find((t) => t.system === "email")?.value || "";
  return {
    name: name || "",
    relationship,
    phone,
    email,
    present: true,
  };
}

export function encounterDate(enc) {
  return enc?.period?.start || "";
}

export function encounterTypeLabel(enc) {
  const typed = enc?.type?.[0];
  return (
    typed?.text ||
    typed?.coding?.[0]?.display ||
    enc?.class?.display ||
    enc?.class?.code ||
    NA
  );
}

export function encounterVisitTypeLabel(enc) {
  const typed = enc?.type?.[0];
  return typed?.text || typed?.coding?.[0]?.display || NA;
}

export function encounterClassLabel(enc) {
  return enc?.class?.display || enc?.class?.code || NA;
}

export function encounterStatusLabel(status) {
  if (!status) return NA;
  if (status === "in-progress") return "In Progress";
  if (status === "finished") return "Completed";
  if (status === "onleave") return "On Leave";
  return status.charAt(0).toUpperCase() + status.slice(1);
}

export function encounterProvider(enc) {
  const p = enc?.participant?.[0]?.individual;
  return p?.display || NA;
}

export function encounterOrganization(enc) {
  return enc?.serviceProvider?.display || NA;
}

export function encounterLocation(enc) {
  return enc?.location?.[0]?.location?.display || NA;
}

export function reasonCodings(enc) {
  const out = [];
  for (const rc of enc?.reasonCode || []) {
    const coding =
      (rc.coding || []).find((c) => c.system === ICD10CM_SYSTEM) || rc.coding?.[0];
    if (!coding?.code) continue;
    out.push({
      code: coding.code,
      display: rc.text || coding.display || "",
      system: coding.system || "",
    });
  }
  return out;
}

export function encounterReasonCodes(enc) {
  const codes = reasonCodings(enc).map((c) => c.code);
  return codes.length ? codes.join(", ") : NA;
}

export function sortEncountersNewest(encounters) {
  return [...(encounters || [])].sort((a, b) => {
    const da = encounterDate(a) || "";
    const db = encounterDate(b) || "";
    return db.localeCompare(da);
  });
}

export function diagnosesFromEncounters(encounters, descriptionByCode = {}) {
  const byCode = new Map();
  for (const enc of encounters || []) {
    const cancelled = enc.status === "cancelled";
    const noted = encounterDate(enc);
    for (const reason of reasonCodings(enc)) {
      const existing = byCode.get(reason.code);
      const description =
        descriptionByCode[reason.code] || reason.display || NA;
      if (!existing) {
        byCode.set(reason.code, {
          code: reason.code,
          description,
          firstNoted: noted || "",
          status: cancelled ? NA : "Active",
        });
      } else {
        if (noted && (!existing.firstNoted || noted < existing.firstNoted)) {
          existing.firstNoted = noted;
        }
        if (!cancelled) existing.status = "Active";
        if (existing.description === NA && description !== NA) {
          existing.description = description;
        }
      }
    }
  }
  return [...byCode.values()].sort((a, b) =>
    (a.firstNoted || "").localeCompare(b.firstNoted || ""),
  );
}

export async function resolveIcdDescriptions(codes) {
  const unique = [...new Set((codes || []).filter(Boolean))];
  const map = {};
  await Promise.all(
    unique.map(async (code) => {
      try {
        const data = await getDiagnosisCodeByCode(code);
        map[code] = data.description || data.shortDescription || "";
      } catch {
        map[code] = "";
      }
    }),
  );
  return map;
}

export function uniqueReasonCodes(encounters) {
  const codes = [];
  for (const enc of encounters || []) {
    for (const reason of reasonCodings(enc)) codes.push(reason.code);
  }
  return [...new Set(codes)];
}

export function encounterNumber(enc) {
  return encounterNumberOf(enc);
}

export function chartPatientFields(patient) {
  const age = ageFromBirthDate(patient?.birthDate);
  const contact = emergencyContactOf(patient);
  return {
    id: patient?.id,
    name: patientFullName(patient) || NA,
    initials: patientInitials(patient),
    mrn: mrnOf(patient),
    age,
    birthDate: patient?.birthDate || "",
    gender: formatGender(patient?.gender),
    administrativeGender: formatGender(patient?.gender),
    active: patient?.active !== false,
    phone: patientPhone(patient),
    email: patientEmail(patient),
    address: formatAddress(patient),
    language: preferredLanguage(patient),
    organization: managingOrganizationName(patient),
    lastUpdated: patient?.meta?.lastUpdated || "",
    ssn: ssnOf(patient),
    marital: maritalLabel(patient),
    race: raceLabel(patient),
    ethnicity: ethnicityLabel(patient),
    birthsex: birthsexLabel(patient),
    genderIdentity: genderIdentityLabel(patient),
    pronouns: pronounsLabel(patient),
    deceased: patient?.deceasedBoolean === true || !!patient?.deceasedDateTime,
    deceasedDate: patient?.deceasedDateTime || "",
    contact,
  };
}
